/**
 * SoaExportButton
 *
 * Button that triggers SoA PDF generation and opens the result in a new tab.
 * Shows loading state while the PDF is being generated (polled via query).
 */

import { useEffect, useRef, useState } from "react";
import { useQuery as useConvexQuery } from "convex/react";
import { Download, Loader2 } from "lucide-react";
import { api } from "convex/_generated/api";
import { useApiMutation } from "@/packages/features-shared";
import { Button } from "@/components/ui/primitives";
import { useTranslation } from "@/packages/utils";
import type { AuditId } from "@/packages/types";
import type { Id } from "convex/_generated/dataModel";

// ─── Props ───────────────────────────────────────────────────────────────────

interface SoaExportButtonProps {
  auditId: AuditId;
  standardId: Id<"officialStandards">;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function SoaExportButton({ auditId, standardId }: SoaExportButtonProps) {
  const { t } = useTranslation("soa");
  const [isGenerating, setIsGenerating] = useState(false);
  const didOpenRef = useRef(false);

  const generateReport = useApiMutation(api.soa_mutations.generateSoaReport, {
    name: "generate-soa-report",
  });

  // Poll for PDF URL once generation is triggered
  const pdfUrl = useConvexQuery(
    api.soa_queries.getSoaPdfUrl,
    isGenerating ? { auditId } : "skip",
  );

  // Open the PDF when the URL is ready
  useEffect(() => {
    if (pdfUrl && isGenerating && !didOpenRef.current) {
      didOpenRef.current = true;
      setIsGenerating(false);
      window.open(pdfUrl, "_blank");
    }
  }, [pdfUrl, isGenerating]);

  const handleExport = () => {
    didOpenRef.current = false;
    setIsGenerating(true);
    generateReport.mutate({ auditId, standardId });
  };

  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={handleExport}
      disabled={isGenerating || generateReport.isPending}
      icon={isGenerating || generateReport.isPending ? Loader2 : Download}
    >
      {isGenerating || generateReport.isPending
        ? t("actions.exportingPdf")
        : t("actions.exportPdf")}
    </Button>
  );
}
